import { useRef, useMemo, useCallback, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

const PARTICLE_COUNT = 60;

function Particles() {
  const { scene } = useThree();
  const pointsRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  const speeds = useMemo(() => {
    const spd = new Float32Array(PARTICLE_COUNT);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      spd[i] = 0.08 + Math.random() * 0.15;
    }
    return spd;
  }, []);

  useEffect(() => {
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    const colors = new Float32Array(PARTICLE_COUNT * 3);

    const goldColor = new THREE.Color("#C8A23A");
    const maroonColor = new THREE.Color("#7A1A31");
    const whiteColor = new THREE.Color("#F5F0E8");

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 30;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 8 - 3;

      const pick = Math.random();
      const c = pick < 0.45 ? goldColor : pick < 0.75 ? maroonColor : whiteColor;
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      vertexColors: true,
      size: 2.5,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geometry, material);
    pointsRef.current = points;
    scene.add(points);

    return () => {
      scene.remove(points);
      geometry.dispose();
      material.dispose();
    };
  }, [scene]);

  const handlePointerMove = useCallback((e) => {
    mouseRef.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
    mouseRef.current.y = -(e.clientY / window.innerHeight - 0.5) * 2;
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handlePointerMove, { passive: true });
    return () => window.removeEventListener("mousemove", handlePointerMove);
  }, [handlePointerMove]);

  useFrame((state, delta) => {
    if (!pointsRef.current) return;
    const posAttr = pointsRef.current.geometry.attributes.position;
    const posArr = posAttr.array;
    const t = state.clock.elapsedTime;

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const i3 = i * 3;
      posArr[i3 + 1] += speeds[i] * delta * 8;
      posArr[i3] += Math.sin(t * 0.3 + i) * 0.003;
      posArr[i3] += mouseRef.current.x * 0.002;
      posArr[i3 + 1] += mouseRef.current.y * 0.001;

      if (posArr[i3 + 1] > 16) {
        posArr[i3 + 1] = -16;
        posArr[i3] = (Math.random() - 0.5) * 20;
      }
    }
    posAttr.needsUpdate = true;
  });

  return null;
}

export const ParticleCanvas = () => {
  return (
    <div
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ opacity: 0.6 }}
      aria-hidden="true"
      data-testid="particle-canvas"
    >
      <Canvas
        dpr={[1, 1.5]}
        camera={{ position: [0, 0, 8], fov: 55 }}
        gl={{ antialias: false, alpha: true, powerPreference: "low-power" }}
        frameloop="always"
        style={{ background: "transparent" }}
      >
        <Particles />
      </Canvas>
    </div>
  );
};
